import _ from 'lodash';
import { useCallback, useMemo } from 'react';

import amplitude from '@/amplitude';
import type { BillingPeriod, SubscriptionPlansData } from '@/features/subscription/types';
import { mapBackendPeriodicityToFrontend, mapBackendSubscriptionPlanToData } from '@/shared/api/mappers/subscription';
import {
  SubscriptionPlanPeriodicity,
  useCreateStripeCheckoutSessionMutation,
  useCreateStripePortalSessionMutation,
  useGetAvailableSubscriptionPlans,
  useGetCurrentUserSubscription,
} from '@/shared/hooks/queries/generated';

export const useSubscriptionPlansData = (billingPeriod: BillingPeriod) => {
  // Queries
  const { data: plansData, isLoading: plansLoading } = useGetAvailableSubscriptionPlans();
  const { data: currentSubscriptionData, isLoading: subscriptionLoading } = useGetCurrentUserSubscription(
    {},
    {
      refetchInterval: 5000,
    },
  );

  // Mutations
  const createCheckoutSession = useCreateStripeCheckoutSessionMutation();

  // Mutation pour créer une session de portail Stripe
  const createPortalSession = useCreateStripePortalSessionMutation();

  // Handle subscription
  const handleSubscribe = useCallback(
    async (planId: string, periodicity: SubscriptionPlanPeriodicity) => {
      try {
        const billingPeriodParam = periodicity === 'monthly' ? 'monthly' : 'yearly';

        const result = await createCheckoutSession.mutateAsync({
          input: {
            planId,
            periodicity,
            successUrl: `${window.location.origin}/subscription?success=true&billing_period=${billingPeriodParam}`,
            cancelUrl: `${window.location.origin}/subscription?billing_period=${billingPeriodParam}`,
          },
        });

        if (result.createStripeCheckoutSession.url) {
          // Tracker l'événement de début de souscription
          amplitude.track('Paiement Started', { planId, periodicity });

          window.location.href = result.createStripeCheckoutSession.url;
        }
      } catch (error) {
        console.error('Error creating checkout session:', error);
        throw error;
      }
    },
    [createCheckoutSession],
  );

  // Handle subscription cancel
  const handleCancel = useCallback(async () => {
    try {
      const result = await createPortalSession.mutateAsync({
        input: {
          type: 'subscriptionCancel',
          returnUrl: window.location.href,
        },
      });

      if (result.createStripePortalSession.url) {
        window.location.href = result.createStripePortalSession.url;
      }
    } catch (error) {
      console.error('Error creating cancellation portal session:', error);
      throw error;
    }
  }, [createPortalSession]);

  // Map current subscription data
  const currentSubscription = currentSubscriptionData?.getCurrentUserSubscription;
  const usedHadSuscribed = !_.isNil(currentSubscription);
  const currentSubscriptionPlanId = currentSubscription?.plan?.id;
  const currentSubscriptionPeriodicity =
    currentSubscription && currentSubscription.plan
      ? mapBackendPeriodicityToFrontend(currentSubscription.plan.periodicity)
      : null;

  // Map plans data
  const subscriptionPlans = useMemo<SubscriptionPlansData | null>(() => {
    if (!plansData?.getAvailableSubscriptionPlans) return null;

    // Find classic and pro plans
    const classicPlanData = plansData.getAvailableSubscriptionPlans.find((p) => p.id === 'recop-classic');
    const proPlanData = plansData.getAvailableSubscriptionPlans.find((p) => p.id === 'recop-pro');

    if (!classicPlanData || !proPlanData) return null;

    // Find custom plans (all plans that are not classic or pro)
    const customPlansData = plansData.getAvailableSubscriptionPlans.filter(
      (p) => p.id !== 'recop-beginner' && p.id !== 'recop-classic' && p.id !== 'recop-pro',
    );

    // Map plans data
    const beginnerPlan = mapBackendSubscriptionPlanToData({
      plan: plansData.getAvailableSubscriptionPlans.find((p) => p.id === 'recop-beginner')!,
      currentPlanId: currentSubscriptionPlanId,
      usedHadSuscribed,
      billingPeriod,
      currentPlanBillingPeriod: currentSubscriptionPeriodicity,
      handleSubscribe,
      handleCancel,
    });

    const classicPlan = mapBackendSubscriptionPlanToData({
      plan: classicPlanData,
      currentPlanId: currentSubscriptionPlanId,
      usedHadSuscribed,
      billingPeriod,
      currentPlanBillingPeriod: currentSubscriptionPeriodicity,
      handleSubscribe,
      handleCancel,
    });

    const proPlan = mapBackendSubscriptionPlanToData({
      plan: proPlanData,
      currentPlanId: currentSubscriptionPlanId,
      usedHadSuscribed,
      billingPeriod,
      currentPlanBillingPeriod: currentSubscriptionPeriodicity,
      handleSubscribe,
      handleCancel,
    });

    const customPlans = customPlansData.map((customPlanData) =>
      mapBackendSubscriptionPlanToData({
        plan: customPlanData,
        currentPlanId: currentSubscriptionPlanId,
        usedHadSuscribed,
        billingPeriod,
        currentPlanBillingPeriod: currentSubscriptionPeriodicity,
        handleSubscribe,
        handleCancel,
      }),
    );

    return {
      beginner: beginnerPlan,
      classic: classicPlan,
      pro: proPlan,
      customPlans,
    };
  }, [
    plansData,
    currentSubscriptionPlanId,
    usedHadSuscribed,
    currentSubscriptionPeriodicity,
    billingPeriod,
    handleSubscribe,
    handleCancel,
  ]);

  return {
    subscriptionPlans,
    currentSubscriptionPeriodicity,
    loading: plansLoading || subscriptionLoading,
    error: null,
  };
};
